import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Add as AddIcon } from '@mui/icons-material';
import { 
    DependentModal,
    RemoveDependentModal,
    Button, 
    Text, 
    Container, 
    ContentSmall, 
    Title, 
    i18n,
    tokens,
    ChangeRequestModal
} from '@simplybenefits/sb-ui-library';
import PersonBlock from '../PersonBlock.js';

import { createChangeRequest, submitChangeRequest, deleteChangeRequest } from '../../../actions/changeRequests.js';
import { createFolder, downloadFile, listFiles, removeFile, uploadFile } from '../../../actions/files.js';

/**
 * Dependents component
 * @param {Object} props
 * @param {Array} props.dependents - List of dependents
 * @param {Object} props.whitelabel - Whitelabel settings
 * @param {Function} props.dispatch - Redux dispatch function
 * @param {Object} props.meta - Meta information
 * @param {Object} props.loader - Loader state
 * @returns {JSX.Element}
 */
const Dependents = ({
    dependents,
    whitelabel,
    dispatch,
    meta,
    loader
}) => {
    const [ dependent, setDependent ] = useState(null);
    const [ isEdit, setIsEdit ] = useState(false);
    const [ showAddDependentForm, setShowAddDependentForm ] = useState(false);
    const [ showRemoveDependent, setRemoveDependent ] = useState(false);
    const [ changeRequestModalOpen, setChangeRequestModalOpen ] = useState(false);
    const [ changeRequestData, setChangeRequestData ] = useState(null);

    /**
     * Reset form and state to initial values
     */
    const goBack = () => {
        setShowAddDependentForm(false);
        setIsEdit(false);
        setRemoveDependent(false);
        setDependent(null);
    };

    /**
     * Show the add dependent form
     */
    const addDependent = () => {
        setShowAddDependentForm(true);
    };

    /**
     * Edit an existing dependent
     * @param {Object} dependentData - Data of the dependent to be edited
     */
    const editDependent = (dependentData) => {
        setIsEdit(true);
        setDependent(dependentData);
        setShowAddDependentForm(true);
    };

    /**
     * Remove a dependent
     * @param {Object} dependentInfo - Data of the dependent to be removed
     */
    const remove = (dependentInfo) => {
        setDependent(dependentInfo);
        setRemoveDependent(true);
    };

    /**
     * Close the change request modal
     */
    const closeChangeRequest = () => {
        setDependent(false);
        setIsEdit(false);
        setChangeRequestModalOpen(false);
    };

    /**
     * Create and open a change request form
     * @param {Object} changeRequestData - Data for creating the change request
     * @returns {Promise<boolean>}
     */
    const openChangeRequestModal = (dependentData) => {
        setShowAddDependentForm(false);
        setChangeRequestModalOpen(true);
        setChangeRequestData(dependentData);
    };

    return (
        <Container>
            <ContentSmall style={{ width: "60%" }}>
                <Title title={ i18n.t(tokens.titles.dependents) } />

                {
                    dependents.length === 0 ?
                        <Text testid="dependents" light medium>{ i18n.t(tokens.labels.dependents.youHaveNotAddedDependentYet)}</Text>
                        :
                        dependents.map((dependentData, i) => (
                            <PersonBlock
                                testid={i === 0 ? "dependents" : undefined}
                                key={dependentData.dependentID}
                                firstname={dependentData.firstname}
                                lastname={dependentData.lastname}
                                relationship={dependentData.relationship}
                                parentID={dependentData.parentID}
                                type="dependent"
                                status={dependentData.status === 'Active' ? 'Active' : dependentData.termDate ? 'Terminated' : 'Disabled'}
                                edit={() => editDependent(dependentData)}
                                remove={() => remove(dependentData)}
                            />
                        ))
                }

                { // hide the button for the SOGS group
                    meta.groupNum !== "62250" ?
                        <Button
                            testid="add-dependent"
                            onClick={ () => addDependent() }
                            transparent
                        >
                            <AddIcon style={{ fontSize: 17, lineHeight: '14px', color: 'inherit', marginRight: 5 }} />
                            { i18n.t(tokens.titles["add dependent"]) }
                        </Button>
                        : null
                }
                
                <DependentModal
                    open={showAddDependentForm}
                    dependentData={ dependent }
                    goBack={ goBack }
                    isEdit={ isEdit }
                    openChangeRequestModal={ (dependentData) => openChangeRequestModal(dependentData) }
                    whitelabel={whitelabel}
                    loader={loader}
                />

                <RemoveDependentModal
                    open={showRemoveDependent}
                    whitelabel={whitelabel}
                    dependentInfo={ dependent }
                    goBack={ goBack }
                    openChangeRequestModal={ (dependentData) => openChangeRequestModal(dependentData) }
                />

                <ChangeRequestModal
                    open={ changeRequestModalOpen }
                    onClose={ closeChangeRequest }
                    createChangeRequest={(...params) => dispatch(createChangeRequest(...params))}
                    deleteChangeRequest={(...params) => dispatch(deleteChangeRequest(...params))}
                    submitChangeRequest={(...params) => dispatch(submitChangeRequest(...params))}
                    changeRequestData={changeRequestData}
                    loader={loader}

                    // Files Props
                    type="users"
                    id={ meta.userID }
                    dispatch={ dispatch }
                    listFiles={ listFiles }
                    createFolder={ createFolder }
                    uploadFile={ uploadFile }
                    downloadFile={ downloadFile }
                    removeFile={ removeFile }
                />
            </ContentSmall>
        </Container>
    );
};

/**
 * Map state to props
 * @param {Object} state - Redux state
 * @returns {Object}
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    meta: state.user.meta,
    dependents: state.user.dependents,
    whitelabel: state.whitelabel
});

export default connect(mapStateToProps)(Dependents);